import { useEffect, useState, useCallback } from "react";
import { Users, UserCheck, Clock, Bot, TrendingUp, ShieldCheck, Loader2, Ban, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getAdminStats, getAdminUsers, approveUser, rejectUser, deleteUser, suspendUser, fmtUsd } from "@/lib/api";

const StatCard = ({ icon: Icon, label, value, accent = "#00FFA3", testid }) => (
  <div data-testid={testid} className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5">
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-faint)]">{label}</span>
      <Icon className="h-4 w-4" style={{ color: accent }} />
    </div>
    <div className="mt-2 font-head font-black text-2xl" style={{ color: accent }}>{value}</div>
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

export const AdminPanel = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmDelId, setConfirmDelId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([getAdminStats(), getAdminUsers()]);
      setStats(s); setUsers(u);
    } catch (e) {
      toast.error("Admin verileri alınamadı: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!confirmDelId) return;
    const t = setTimeout(() => setConfirmDelId(null), 4000);
    return () => clearTimeout(t);
  }, [confirmDelId]);

  const doApprove = async (id) => {
    setBusyId(id);
    try { await approveUser(id); toast.success("Kullanıcı onaylandı."); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Onaylanamadı"); }
    finally { setBusyId(null); }
  };
  const doReject = async (id) => {
    setBusyId(id);
    try { await rejectUser(id); toast("Kullanıcı başvurusu reddedildi."); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusyId(null); }
  };
  const doSuspend = async (id) => {
    setBusyId(id);
    try { await suspendUser(id); toast("Kullanıcı askıya alındı."); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusyId(null); }
  };
  const doDelete = async (id) => {
    if (confirmDelId !== id) { setConfirmDelId(id); return; }
    setBusyId(id);
    try { await deleteUser(id); toast.success("Kullanıcı kalıcı olarak silindi."); setConfirmDelId(null); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
    finally { setBusyId(null); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[var(--pos)]" /></div>;
  }

  const pnl = stats?.aggregate_realized_pnl ?? 0;

  const renderActions = (u) => {
    if (u.role === "admin") return <span className="text-xs text-[var(--text-faint)]">—</span>;
    if (u.active) return (
      <Button data-testid={`suspend-${u.email}`} size="sm" variant="outline" disabled={busyId === u.id}
        onClick={() => doSuspend(u.id)}
        className="h-9 border-[#FF3366]/40 text-[var(--neg)] hover:bg-[#FF3366]/10 hover:text-[var(--neg)]">
        {busyId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Ban className="h-3.5 w-3.5 mr-1" /> Askıya Al</>}
      </Button>
    );
    return (
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <Button data-testid={`approve-${u.email}`} size="sm" disabled={busyId === u.id}
          onClick={() => doApprove(u.id)}
          className="h-9 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
          {busyId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5 mr-1" /> Onayla</>}
        </Button>
        {!u.rejected && (
          <Button data-testid={`reject-${u.email}`} size="sm" variant="outline" disabled={busyId === u.id}
            onClick={() => doReject(u.id)}
            className="h-9 border-[#FF3366]/40 text-[var(--neg)] hover:bg-[#FF3366]/10 hover:text-[var(--neg)]">
            <X className="h-3.5 w-3.5 mr-1" /> Reddet
          </Button>
        )}
        {u.rejected && (
          <Button data-testid={`delete-${u.email}`} size="sm" variant="outline" disabled={busyId === u.id}
            onClick={() => doDelete(u.id)}
            className="h-9 border-[#FF3366]/40 text-[var(--neg)] hover:bg-[#FF3366]/10 hover:text-[var(--neg)]">
            {busyId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : confirmDelId === u.id ? <>Emin misiniz? Sil</> : <><Trash2 className="h-3.5 w-3.5 mr-1" /> Sil</>}
          </Button>
        )}
      </div>
    );
  };

  const statusBadge = (u) => u.active
    ? <span className="text-xs text-[var(--pos)]">● Aktif</span>
    : u.rejected ? <span className="text-xs text-[var(--neg)]">● Reddedildi</span>
    : <span className="text-xs text-[var(--warn)]">● Onay Bekliyor</span>;

  return (
    <div className="space-y-6" data-testid="admin-panel">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-[var(--accent)]" />
        <h2 className="font-head font-bold text-xl">Admin Paneli</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard testid="stat-total-users" icon={Users} label="Toplam Kullanıcı" value={stats?.total_users ?? 0} />
        <StatCard testid="stat-active-users" icon={UserCheck} label="Aktif" value={stats?.active_users ?? 0} accent="#00FFA3" />
        <StatCard testid="stat-pending-users" icon={Clock} label="Onay Bekleyen" value={stats?.pending_users ?? 0} accent="#FFB020" />
        <StatCard testid="stat-active-bots" icon={Bot} label="Aktif Bot" value={stats?.active_bots ?? 0} accent="#A374FF" />
        <StatCard testid="stat-total-pnl" icon={TrendingUp} label="Toplam Realize K/Z"
          value={fmtUsd(pnl)} accent={pnl >= 0 ? "#00FFA3" : "#FF3366"} />
      </div>

      {/* Users table */}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--border)]">
          <h3 className="font-head font-bold text-sm">Kullanıcılar & Belge Onayları</h3>
          <p className="text-xs text-[var(--text-faint)] mt-0.5">Kullanım Koşulları · Risk Feragatnamesi · KVKK onay tarihleri</p>
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] border-b border-[var(--border)]">
                <th className="text-left font-medium px-5 py-3">Kullanıcı</th>
                <th className="text-left font-medium px-3 py-3">Durum</th>
                <th className="text-left font-medium px-3 py-3">Onaylar</th>
                <th className="text-left font-medium px-3 py-3">Onay Tarihi</th>
                <th className="text-right font-medium px-5 py-3">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const c = u.consents || {};
                const allConsent = c.terms && c.risk && c.kvkk;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}
                    className="border-b border-[var(--surface-hover)] hover:bg-[var(--surface-hover)]">
                    <td className="px-5 py-3">
                      <div className="font-medium text-[var(--text-primary)]">{u.name || "—"}</div>
                      <div className="text-xs text-[var(--text-faint)] font-mono">{u.email}</div>
                      {u.role === "admin" && (
                        <span className="text-[10px] text-[var(--accent)] font-mono">YÖNETİCİ</span>
                      )}
                    </td>
                    <td className="px-3 py-3">{statusBadge(u)}</td>
                    <td className="px-3 py-3">
                      <span className={`text-xs font-mono ${allConsent ? "text-[var(--pos)]" : "text-[var(--text-faint)]"}`}>
                        {allConsent ? "3/3 tamam" : "—"}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-[var(--text-dim)] font-mono">{fmtDate(c.accepted_at)}</td>
                    <td className="px-5 py-3 text-right">{renderActions(u)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile user cards */}
        <div className="md:hidden divide-y divide-[var(--border)]">
          {users.map((u) => {
            const c = u.consents || {};
            const allConsent = c.terms && c.risk && c.kvkk;
            return (
              <div key={u.id} data-testid={`user-card-${u.email}`} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-[var(--text-primary)] truncate">{u.name || "—"}</div>
                    <div className="text-xs text-[var(--text-faint)] font-mono truncate">{u.email}</div>
                    {u.role === "admin" && <span className="text-[10px] text-[var(--accent)] font-mono">YÖNETİCİ</span>}
                  </div>
                  {statusBadge(u)}
                </div>
                <div className="flex items-center gap-3 text-xs font-mono text-[var(--text-faint)]">
                  <span className={allConsent ? "text-[var(--pos)]" : ""}>Onay: {allConsent ? "3/3" : "—"}</span>
                  <span>·</span>
                  <span>{fmtDate(c.accepted_at)}</span>
                </div>
                <div>{renderActions(u)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-user performance */}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--border)]">
          <h3 className="font-head font-bold text-sm">Kullanıcı Performansı</h3>
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-perf-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] border-b border-[var(--border)]">
                <th className="text-left font-medium px-5 py-3">Kullanıcı</th>
                <th className="text-right font-medium px-3 py-3">Portföy Değeri</th>
                <th className="text-right font-medium px-3 py-3">Toplam K/Z</th>
                <th className="text-right font-medium px-3 py-3">Açık Poz.</th>
                <th className="text-right font-medium px-5 py-3">Bot</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.users || []).map((u) => (
                <tr key={u.id} className="border-b border-[var(--surface-hover)] hover:bg-[var(--surface-hover)]">
                  <td className="px-5 py-3">
                    <div className="text-[var(--text-primary)] text-xs">{u.name}</div>
                    <div className="text-[11px] text-[var(--text-faint)] font-mono">{u.email}</div>
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-[var(--text-primary)]">{fmtUsd(u.total_value)}</td>
                  <td className={`px-3 py-3 text-right font-mono ${u.total_pnl >= 0 ? "text-[var(--pos)]" : "text-[var(--neg)]"}`}>
                    {u.total_pnl >= 0 ? "+" : ""}{fmtUsd(u.total_pnl)} ({u.total_pnl_pct}%)
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-[var(--text-dim)]">{u.open_positions + u.futures_positions}</td>
                  <td className="px-5 py-3 text-right">
                    {u.bot_active
                      ? <span className="text-xs text-[var(--pos)]">● {u.bot_market === "futures" ? "Kaldıraçlı" : "Spot"}</span>
                      : <span className="text-xs text-[var(--text-faint)]">Pasif</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile perf cards */}
        <div className="md:hidden divide-y divide-[var(--border)]">
          {(stats?.users || []).map((u) => (
            <div key={u.id} className="p-4 space-y-2" data-testid={`perf-card-${u.email}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[var(--text-primary)] text-xs truncate">{u.name}</div>
                  <div className="text-[11px] text-[var(--text-faint)] font-mono truncate">{u.email}</div>
                </div>
                {u.bot_active
                  ? <span className="text-xs text-[var(--pos)] flex-shrink-0">● {u.bot_market === "futures" ? "Kaldıraçlı" : "Spot"}</span>
                  : <span className="text-xs text-[var(--text-faint)] flex-shrink-0">Pasif</span>}
              </div>
              <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-xs font-mono">
                <div className="flex justify-between"><span className="text-[var(--text-faint)]">Portföy</span><span className="text-[var(--text-primary)]">{fmtUsd(u.total_value)}</span></div>
                <div className="flex justify-between"><span className="text-[var(--text-faint)]">Açık Poz.</span><span className="text-[var(--text-dim)]">{u.open_positions + u.futures_positions}</span></div>
                <div className="flex justify-between col-span-2"><span className="text-[var(--text-faint)]">Toplam K/Z</span>
                  <span className={u.total_pnl >= 0 ? "text-[var(--pos)]" : "text-[var(--neg)]"}>
                    {u.total_pnl >= 0 ? "+" : ""}{fmtUsd(u.total_pnl)} ({u.total_pnl_pct}%)
                  </span></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
